// Pure helper functions for the component-library version of Manage
// Expiring Leases (property-level). No hooks, no components — every
// function here takes plain data in and returns plain data out.
import moment from 'moment';
import { dateDiffInDays } from '../../utils/date-helpers';
import {
  EXPIRATION_BUCKETS,
  DEFAULT_HIDDEN_BUCKET_ID,
  RENEWAL_STATUS_OPTIONS,
} from './constantsV2';

// Days between today and a lease's end date, using the same calendar-day
// difference the legacy row and info cards use. Positive when the end date
// is in the future, negative when it has already passed.
export const getDaysUntilLeaseEnd = (endDate, today) =>
  dateDiffInDays(new Date(today), new Date(endDate));

// Returns the id of the bucket a day count falls in, or undefined if none
// match (only possible for a non-numeric input).
export const classifyExpirationBucket = (daysUntilLeaseEnd) => {
  const bucket = EXPIRATION_BUCKETS.find(({ test }) => test(daysUntilLeaseEnd));
  return bucket ? bucket.id : undefined;
};

// True when a day count falls in the bucket the default view hides.
export const isHiddenByDefault = (daysUntilLeaseEnd) =>
  classifyExpirationBucket(daysUntilLeaseEnd) === DEFAULT_HIDDEN_BUCKET_ID;

// Ports the legacy `getCsvStatus` string logic exactly: the on-screen status
// dot's `isCsv: true` branch. Depends only on the two booleans the legacy
// code uses. Can only return 'Completed', 'In Process' or '---' — it can
// never return 'Not Started' or 'On Notice', matching the legacy behavior.
export const getRenewalStatusLabel = ({ isRenewal, isRenewalComplete }) => {
  if (isRenewal && isRenewalComplete) {
    return RENEWAL_STATUS_OPTIONS[0].text; // 'Completed'
  }
  if (isRenewal && !isRenewalComplete) {
    return RENEWAL_STATUS_OPTIONS[1].text; // 'In Process'
  }
  return '---';
};

// The exact unit-status text the backend compares against. Ported from
// `filterUnitsRenewalStatus` at `fortress-backend/src/domain/command-handlers/Leases.js`.
export const ON_NOTICE_TEXT = 'Occupied / On Notice to Vacate';

// Ports the backend's `filterUnitsRenewalStatus` predicate exactly (see the
// conversion addendum). The on-screen renewal-status label can only ever be
// 'Completed', 'In Process' or '---', so it cannot drive this four-way
// filter on its own — this reads the same fields the server reads instead:
// the unit's status description, and `isRenewal`/`isRenewalComplete` off
// the row's last lease. `statusName` is matched case-insensitively, since
// a caller may pass it through the library's own lowercased filter values.
export const matchesRenewalStatus = (row, statusName) => {
  const normalizedStatus = String(statusName || '').toLowerCase();
  const isOnNotice = row?.unitStatus?.description === ON_NOTICE_TEXT;

  if (normalizedStatus === 'onnotice') {
    return isOnNotice;
  }

  if (isOnNotice) {
    // Deliberate: a row that is on notice AND has a renewal matches
    // whichever of the other three statuses was asked for. This looks
    // wrong. It is the current backend behavior, so it stays here too.
    return Boolean(row?.isRenewal);
  }

  const isRenewal = Boolean(row?.isRenewal);
  const isRenewalComplete = Boolean(row?.isRenewalComplete);

  if (normalizedStatus === 'completed') {
    return isRenewal && isRenewalComplete;
  }
  if (normalizedStatus === 'inprocess') {
    return isRenewal && !isRenewalComplete;
  }
  if (normalizedStatus === 'incomplete') {
    return !isRenewal;
  }
  return false;
};

// Short, capitalized English month abbreviations moment recognizes, e.g.
// 'Sep'. Matches the legacy `moment.monthsShort()` check exactly.
const isShortMonthName = (month) => moment.monthsShort().includes(month);

// Reads the three URL drill-down shapes described in the legacy spec and
// returns only the keys a link actually supplied. Returns an empty object
// when the URL carries none of the three, so the caller keeps its own
// defaults instead of having them cleared.
export const parseDrillDownParams = (search) => {
  const params = new URLSearchParams(search);
  const month = params.get('month');
  const year = params.get('year');
  const renewalStatus = params.get('renewalStatus');

  const result = {};

  if (month === 'mtm') {
    result.bucketId = 'monthToMonth';
  } else if (
    month &&
    isShortMonthName(month) &&
    year &&
    parseInt(year, 10) >= 0
  ) {
    result.expirationRange = {
      from: moment().utc().year(year).month(month).startOf('month'),
      to: moment().utc().year(year).month(month).endOf('month'),
    };
  }

  if (renewalStatus) {
    result.renewalStatus = renewalStatus;
  }

  return result;
};

export const NO_VALUE = '---';

/**
 * Builds a currency cell for the rent columns.
 *
 * The legacy table shows a dash rather than a zero here, so a missing amount reads
 * as no data instead of a real rent of zero. Preserve that. The portfolio table
 * renders a bare currency value, which would turn every missing amount into $0.00.
 */
export const currencyCell = (value) => {
  const amount = parseFloat(value);

  return !Number.isNaN(amount) && amount > 0
    ? { variant: 'currency', value }
    : { value: NO_VALUE };
};

/**
 * Builds the balance cell. Balance keeps its own rule, matching the legacy table:
 * any non-zero balance shows, including a negative one, and zero or missing shows
 * a dash.
 */
export const balanceCell = (value) =>
  value ? { variant: 'currency', value } : { value: NO_VALUE };

/**
 * Returns the rows in scope for the current quick-filter card.
 *
 * Three cases, in order:
 * 1. A card is selected, so keep only the rows in that bucket.
 * 2. No card, but the page was opened from a drill-down carrying a date range or
 *    a renewal status. Keep every row, because that drill-down already narrowed
 *    them and the day-bucket rule must not hide rows past 120 days on top.
 * 3. No card and no such drill-down, so hide rows more than 120 days out. This is
 *    the default the legacy screen applied through its filter drawer.
 *
 * This is also the set the "Items Found" total counts, so a user who has filtered
 * nothing sees the same number on both sides of the count.
 */
export const selectQuickFilterRows = ({
  rows = [],
  selectedBucketId = null,
  keepAllDays = false,
}) => {
  if (selectedBucketId) {
    return rows.filter(
      (row) =>
        classifyExpirationBucket(row.daysUntilLeaseEnd) === selectedBucketId,
    );
  }

  if (keepAllDays) return rows;

  return rows.filter((row) => !isHiddenByDefault(row.daysUntilLeaseEnd));
};
